<template>
    <Teleport to="body">
        <div
            v-if="props.isOpen"
            ref="dialogElement"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="props.labelId"
            class="z-50 flex h-128 min-h-80 w-md max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl bg-gray-50 text-gray-700 shadow-md ring-1 ring-gray-300"
            :style="floatingStyles"
            @keydown.esc="deactivate({ returnFocus: true })"
        >
            <div
                class="relative z-10 border-b border-gray-200 p-1.5 transition-shadow"
                :class="{ 'shadow-md': !arrivedState.top }"
            >
                <label :for="inputId" class="sr-only">{{ $t('filter-users') }}</label>

                <div class="relative">
                    <SearchIcon class="pointer-events-none absolute inset-y-0 left-2.5 my-auto size-5 text-gray-500" />
                    <input
                        :id="inputId"
                        ref="inputElement"
                        v-model="query"
                        type="text"
                        role="combobox"
                        autocomplete="off"
                        aria-autocomplete="list"
                        aria-expanded="true"
                        :aria-controls="listboxId"
                        :aria-activedescendant="activeOptionId"
                        :placeholder="$t('filter-users-placeholder')"
                        class="block w-full rounded-md border-gray-300 bg-white py-2 pl-9 text-sm placeholder:text-gray-500 focus:border-gray-300 focus:ring-0 focus:outline-2 focus:-outline-offset-1 focus:outline-sky-700"
                        :class="query ? 'pr-36' : 'pr-3'"
                        @keydown="onInputKeydown"
                    />
                    <div v-if="query" class="absolute inset-y-0 right-0.75 flex items-center">
                        <Button size="Small" variant="Neutral-Strong" @click="clearQuery">
                            <template #icon>
                                <DeleteForeverIcon class="size-4.5" />
                            </template>
                            {{ $t('delete-filter') }}
                        </Button>
                    </div>
                </div>
            </div>

            <div ref="scrollElement" class="min-h-0 grow scroll-py-1.5 overflow-y-auto py-1.5">
                <!-- Always rendered so screen readers announce changes like the number of results -->
                <div role="status" class="sr-only">{{ statusText }}</div>

                <div :id="listboxId" ref="listboxElement" role="listbox" :aria-label="$t('users')">
                    <ul
                        role="group"
                        :aria-label="assignedTitle"
                        class="mb-1.5 flex flex-col border-b border-gray-300 pb-1.5"
                    >
                        <li aria-hidden="true" :class="TITLE_CLASSES">{{ assignedTitle }}</li>

                        <slot name="assigned" />

                        <li v-if="props.assignedCount === 0" aria-hidden="true" :class="MESSAGE_CLASSES">
                            {{ props.hasAssignedUsers ? $t('no-assigned-users-found') : $t('no-users-assigned') }}
                        </li>
                    </ul>
                    <ul role="group" :aria-label="unassignedTitle" class="flex flex-col">
                        <li aria-hidden="true" :class="TITLE_CLASSES">{{ unassignedTitle }}</li>
                        <slot name="unassigned" />
                        <li v-if="props.unassignedCount === 0" aria-hidden="true" :class="MESSAGE_CLASSES">
                            {{ $t('no-unassigned-users-found') }}
                        </li>
                    </ul>
                </div>

                <div v-if="$slots.actions">
                    <slot name="actions" />
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { autoUpdate, offset, shift, size, useFloating } from '@floating-ui/vue';
import { useScroll } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useFluent } from 'fluent-vue';
import { computed, provide, ref, toRef, useId, useTemplateRef, watch } from 'vue';
import Button from '../Button/Button.vue';
import DeleteForeverIcon from '../Icon/DeleteForeverIcon.vue';
import SearchIcon from '../Icon/SearchIcon.vue';
import { userReferenceDialogKey } from './injectionKeys';

type UserReferenceDialogProps = {
    labelId: string;
    isOpen: boolean;
    anchorElement: HTMLElement | null;
    assignDisabledExplanation?: string;
    assignedCount: number;
    unassignedCount: number;
    hasAssignedUsers: boolean;
};

type UserReferenceDialogEmits = {
    (e: 'close'): void;
};

const props = defineProps<UserReferenceDialogProps>();
const emit = defineEmits<UserReferenceDialogEmits>();
const query = defineModel<string>('query', { default: '' });

const { $t } = useFluent();

const TITLE_CLASSES = 'px-2.5 pt-1 pb-1.5 text-xs font-semibold tracking-wide text-gray-500 uppercase';
const MESSAGE_CLASSES = 'px-2.5 py-1 text-sm font-light text-gray-500';

const assignedTitle = computed(() => $t('assigned-users-count', { count: props.assignedCount }));
const unassignedTitle = computed(() => $t('unassigned-users-count', { count: props.unassignedCount }));

const statusText = computed(() => {
    const count = props.assignedCount + props.unassignedCount;
    return count === 0 ? $t('no-users-found') : $t('users-found', { count });
});

const inputId = `user-reference-search-${useId()}`;
const listboxId = `user-reference-listbox-${useId()}`;

const dialogElement = useTemplateRef('dialogElement');
const inputElement = useTemplateRef('inputElement');
const listboxElement = useTemplateRef('listboxElement');
const scrollElement = useTemplateRef('scrollElement');

const { arrivedState } = useScroll(scrollElement);

const OFFSET_IN_PX = 4;
const VIEWPORT_PADDING_IN_PX = 8;
const MIN_HEIGHT_IN_REM = 20;

const placement = ref<'top-start' | 'bottom-start'>('top-start');

// Decided once per opening instead of by the flip middleware, so the dialog doesn't jump sides while results change
function updatePlacement() {
    if (!props.anchorElement) {
        return;
    }
    const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const spaceAbove = props.anchorElement.getBoundingClientRect().top - OFFSET_IN_PX - VIEWPORT_PADDING_IN_PX;
    placement.value = spaceAbove >= MIN_HEIGHT_IN_REM * remInPx ? 'top-start' : 'bottom-start';
}

const { floatingStyles } = useFloating(
    toRef(() => props.anchorElement),
    dialogElement,
    {
        open: toRef(() => props.isOpen),
        strategy: 'fixed',
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(OFFSET_IN_PX),
            shift({ padding: VIEWPORT_PADDING_IN_PX }),
            size({
                padding: VIEWPORT_PADDING_IN_PX,
                // A fixed height (h-128) keeps the dialog from jumping while filtering, shrunk only to fit the viewport
                // down to min-h-80, which wins over this max-height
                apply({ availableHeight, elements }) {
                    elements.floating.style.maxHeight = `${availableHeight}px`;
                },
            }),
        ],
    },
);

// Focus returns to the trigger on Escape, but not on a click outside, which should keep its own focus
const { deactivate } = useFocusTrap(dialogElement, {
    immediate: true,
    initialFocus: () => inputElement.value ?? false,
    escapeDeactivates: false,
    returnFocusOnDeactivate: false,
    clickOutsideDeactivates: (event) => !isOnAnchor(event),
    // Lets the trigger close the dialog itself instead of reopening it right after the click outside closed it
    allowOutsideClick: isOnAnchor,
    // Only once focus is back on the trigger, as that focus can open the trigger's tooltip
    onPostDeactivate: () => emit('close'),
});

function isOnAnchor(event: Event) {
    return event.target instanceof Node && Boolean(props.anchorElement?.contains(event.target));
}

watch(
    () => props.isOpen,
    (isOpen) => {
        if (isOpen) {
            updatePlacement();
        } else {
            deactivate({ returnFocus: true });
        }
    },
);

const activeOptionId = ref<string>();

watch(query, () => {
    activeOptionId.value = undefined;
});

function clearQuery() {
    query.value = '';
    inputElement.value?.focus();
}

function getOptions() {
    return Array.from(listboxElement.value?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
}

function activateOption(option: HTMLElement) {
    activeOptionId.value = option.id;
    // All the way up for the first option, so the section title above it stays visible
    if (option === getOptions()[0]) {
        scrollElement.value?.scrollTo({ top: 0 });
    } else {
        option.scrollIntoView({ block: 'nearest' });
    }
}

function onInputKeydown(event: KeyboardEvent) {
    switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
            event.preventDefault();
            const options = getOptions();
            if (options.length === 0) {
                return;
            }
            const index = options.findIndex((option) => option.id === activeOptionId.value);
            const nextIndex =
                event.key === 'ArrowDown' ? (index + 1) % options.length : (index <= 0 ? options.length : index) - 1;
            activateOption(options[nextIndex]);
            break;
        }

        case 'Home':
        case 'End': {
            event.preventDefault();
            const options = getOptions();
            if (options.length === 0) {
                return;
            }
            activateOption(event.key === 'Home' ? options[0] : options[options.length - 1]);
            break;
        }

        case 'Enter': {
            const activeOption = getOptions().find((option) => option.id === activeOptionId.value);
            if (activeOption) {
                event.preventDefault();
                activeOption.click();
            }
            break;
        }
    }
}

provide(userReferenceDialogKey, {
    query,
    assignDisabledExplanation: toRef(() => props.assignDisabledExplanation),
    activeOptionId,
    setActiveOption(optionId) {
        activeOptionId.value = optionId;
    },
    clearActiveOption(optionId) {
        if (activeOptionId.value === optionId) {
            activeOptionId.value = undefined;
        }
    },
});
</script>
